/**
 * User comments: shared panel (profile tab, etc.) and modal wrapper (optional).
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, Clock, CheckCircle, XCircle, MessageSquare, Star, ChevronDown } from 'lucide-react';
import { LearnerInlineContentLoader } from './LearnerInlineContentLoader';
import { commentService, LessonCommentDto, LibraryBookCommentDto } from '../../services/commentService';
import { authService } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../feedback/NotificationSystem';
import { useResponsive } from './MobileResponsive';
import { CommentModal } from '../learning/CommentModal';
import { useAppLocale } from '../../providers/LocaleProvider';
import { formatAppDate } from '../../utils/localeDateFormat';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { FlowContextBreadcrumb } from './FlowContextBreadcrumb';
import {
  countCommentDescendantsById,
  enrichCommentsWithParentWriterNames,
  isProfileCommentListRow,
  matchesProfileCommentStatusFilter
} from '../../utils/commentThread';
import {
  schedulePaneHeightSync,
  scrollChildIntoHorizontalScroller,
  applyTabIndicator,
  settleCarouselDrag,
  TAB_CAROUSEL_TRANSITION
} from '../../utils/scrollChildIntoHorizontalScroller';

type ProfileCommentItem =
  | { source: 'lesson'; comment: LessonCommentDto }
  | { source: 'book'; comment: LibraryBookCommentDto };

export interface UserCommentsPanelProps {
  /** Opens the lesson only (e.g. "Click to view lesson" link). Row tap opens comments modal in-place. */
  onNavigateToLesson?: (lessonId: number) => void;
  /** Opens the library book (e.g. from comments tab link). */
  onNavigateToBook?: (bookId: number) => void;
  /** e.g. close modal after navigating from a row */
  onAfterNavigateToLesson?: () => void;
  /** When false, skip fetch and clear list */
  active?: boolean;
  className?: string;
  /**
   * If true, the panel is used inside the profile tab carousel: no inner vertical scrollport —
   * avoid overflow-x-auto with overflow-y visible (CSS forces overflow-y: auto). Page scroll only.
   */
  embedded?: boolean;
}

const COMMENT_FILTER_ORDER = ['all', 'pending', 'approved', 'rejected'] as const;
type CommentFilter = (typeof COMMENT_FILTER_ORDER)[number];

export const UserCommentsPanel: React.FC<UserCommentsPanelProps> = ({
  onNavigateToLesson,
  onNavigateToBook,
  onAfterNavigateToLesson,
  active = true,
  className = '',
  embedded = false
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const { showError } = useNotifications();
  const { t } = useTranslation('learning');
  const { locale, direction } = useAppLocale();
  const { pickTitle, pickBookTitle } = useLocalizedContent();
  const isRtl = direction === 'rtl';

  const buildLessonContextPath = useCallback((comment: LessonCommentDto) => {
    const enSegments: string[] = [];
    const faSegments: string[] = [];
    if (comment.courseTitle) {
      enSegments.push(comment.courseTitle);
      if (comment.courseTitleFa?.trim()) faSegments.push(comment.courseTitleFa.trim());
    }
    if (comment.levelTitle) {
      enSegments.push(comment.levelTitle);
      if (comment.levelTitleFa?.trim()) faSegments.push(comment.levelTitleFa.trim());
    }
    if (enSegments.length === 0) return null;
    return {
      contextSubtitle: enSegments.join(' › '),
      contextSubtitleFa: faSegments.length > 0 ? faSegments.join(' › ') : undefined
    };
  }, []);

  const displayLessonTitle = useCallback(
    (comment: LessonCommentDto) =>
      pickTitle({ title: comment.lessonTitle, titleFa: comment.lessonTitleFa }) ||
      `Lesson ${comment.lessonId}`,
    [pickTitle]
  );

  const displayBookTitle = useCallback(
    (comment: LibraryBookCommentDto) =>
      pickBookTitle({ title: comment.bookTitle, farsiTitle: comment.bookFarsiTitle }) ||
      `Book ${comment.libraryBookId}`,
    [pickBookTitle]
  );

  const filterVisualOrder = useCallback(
    (filterOption: CommentFilter) => {
      const idx = COMMENT_FILTER_ORDER.indexOf(filterOption);
      return isRtl ? COMMENT_FILTER_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );
  const filterCarouselIndex = useCallback(
    (filterOption: CommentFilter) => {
      const idx = COMMENT_FILTER_ORDER.indexOf(filterOption);
      return isRtl ? COMMENT_FILTER_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );
  const filterCarouselOrder = useMemo(
    () => (isRtl ? [...COMMENT_FILTER_ORDER].reverse() : COMMENT_FILTER_ORDER),
    [isRtl]
  );

  const formatCommentDate = (dateString: string) =>
    formatAppDate(dateString, locale, { year: 'numeric', month: 'short', day: 'numeric' });

  const renderViewLinkLabel = (label: string) => (
    <>
      <span>{label}</span>
      <span aria-hidden="true">{direction === 'rtl' ? '←' : '→'}</span>
    </>
  );

  const viewLinkClass =
    'inline-flex shrink-0 items-center gap-1 text-start text-xs font-medium hover:underline';

  const sessionUserId = user?.id ?? authService.getCurrentUser()?.id;
  const sessionReady = authService.isAuthenticated() && !!sessionUserId;

  const [allLessonComments, setAllLessonComments] = useState<LessonCommentDto[]>([]);
  const [allBookComments, setAllBookComments] = useState<LibraryBookCommentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<CommentFilter>('all');
  const [visibleCounts, setVisibleCounts] = useState<Record<CommentFilter, number>>({
    all: 10,
    pending: 10,
    approved: 10,
    rejected: 10
  });
  const [replyCounts, setReplyCounts] = useState<Record<string, number>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [filterHeights, setFilterHeights] = useState<Partial<Record<CommentFilter, number>>>({});
  const filterRefs = useRef<Partial<Record<CommentFilter, HTMLButtonElement>>>({});
  const filterNavRef = useRef<HTMLElement>(null);
  const filterNavRowRef = useRef<HTMLDivElement | null>(null);
  const filterIndicatorElRef = useRef<HTMLDivElement | null>(null);
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const filterContentRefs = useRef<Partial<Record<CommentFilter, HTMLDivElement | null>>>({});
  const filterRef = useRef(filter);
  const activeFilterRef = useRef<CommentFilter>('all');
  const dragOffsetPxRef = useRef(0);
  const dragUiRef = useRef({ isDragging: false, dragOffsetPx: 0 });
  const isRtlRef = useRef(isRtl);
  const filterCarouselIndexRef = useRef(filterCarouselIndex);
  filterCarouselIndexRef.current = filterCarouselIndex;
  const [commentModalLesson, setCommentModalLesson] = useState<{
    lessonId: number;
    lessonTitle?: string;
    focusCommentId?: number;
  } | null>(null);
  const [commentModalBook, setCommentModalBook] = useState<{
    libraryBookId: number;
    bookTitle?: string;
    focusCommentId?: number;
  } | null>(null);

  filterRef.current = filter;

  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  const handleFilterChange = useCallback((next: CommentFilter) => {
    if (next === filterRef.current) return;
    const track = cardsScrollRef.current;
    dragOffsetPxRef.current = 0;
    dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
    if (track) {
      track.style.transition = TAB_CAROUSEL_TRANSITION;
      track.style.setProperty('--comments-filter-drag-x', '0px');
    }
    setIsDragging(false);
    activeFilterRef.current = next;
    setFilter(next);
    updateIndicatorRef.current();
  }, []);

  const updateIndicator = useCallback(() => {
    const row = filterNavRowRef.current;
    if (!row) return;
    const activeFilter = activeFilterRef.current;
    const activeButton = filterRefs.current[activeFilter];
    if (!activeButton) return;

    const { isDragging: dragging, dragOffsetPx: drag } = dragUiRef.current;
    const cards = cardsScrollRef.current;
    const cardWidth = cards?.clientWidth ?? 0;
    const idx = COMMENT_FILTER_ORDER.indexOf(activeFilter);

    const measureInRow = (btn: HTMLElement) => {
      const rowRect = row.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return { left: btnRect.left - rowRect.left, width: btnRect.width };
    };

    let { left, width } = measureInRow(activeButton);

    if (dragging && cardWidth > 0) {
      const progress = isRtlRef.current ? drag / cardWidth : -drag / cardWidth;
      let neighborIdx = idx;
      if (progress > 0 && idx < COMMENT_FILTER_ORDER.length - 1) neighborIdx = idx + 1;
      else if (progress < 0 && idx > 0) neighborIdx = idx - 1;

      const neighborFilter = COMMENT_FILTER_ORDER[neighborIdx];
      const neighborButton = neighborFilter ? filterRefs.current[neighborFilter] : undefined;
      if (neighborButton && neighborIdx !== idx) {
        const t = Math.min(1, Math.abs(progress));
        const neighbor = measureInRow(neighborButton);
        left = left + (neighbor.left - left) * t;
        width = width + (neighbor.width - width) * t;
      }
    }

    applyTabIndicator(filterIndicatorElRef.current, left, width, !dragging);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateIndicatorRef = useRef(updateIndicator);
  updateIndicatorRef.current = updateIndicator;

  const readFilterPaneHeight = useCallback((target: HTMLElement) => {
    return Math.ceil(Math.max(target.scrollHeight, target.offsetHeight, target.getBoundingClientRect().height));
  }, []);

  const fetchAllPages = useCallback(
    async <T extends { id: number; createdAt: string }>(
      fetchPage: (pageNumber: number, pageSize: number) => Promise<Record<string, unknown>>
    ): Promise<T[]> => {
      const pageSize = 200;
      const merged: T[] = [];
      const seen = new Set<number>();
      let pageNumber = 1;

      while (pageNumber <= 40) {
        const result = await fetchPage(pageNumber, pageSize);
        const batch = (result.comments ?? result.Comments ?? []) as T[];
        for (const c of batch) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            merged.push(c);
          }
        }

        const size =
          typeof result.pageSize === 'number' && result.pageSize > 0
            ? result.pageSize
            : typeof result.PageSize === 'number' && result.PageSize > 0
              ? result.PageSize
              : pageSize;
        const totalCount =
          typeof result.totalCount === 'number'
            ? result.totalCount
            : typeof result.TotalCount === 'number'
              ? result.TotalCount
              : undefined;
        const moreByTotal =
          typeof totalCount === 'number' && !Number.isNaN(totalCount)
            ? pageNumber * size < totalCount
            : false;
        const moreByFlag = result.hasMore === true || result.HasMore === true;
        const moreByFullPage = batch.length === pageSize && totalCount === undefined;

        if (batch.length === 0) break;
        if (!moreByTotal && !moreByFlag && !moreByFullPage) break;

        pageNumber += 1;
      }

      return merged;
    },
    []
  );

  const loadComments = useCallback(async () => {
    const userId = user?.id ?? authService.getCurrentUser()?.id;
    if (!authService.isAuthenticated() || !userId) {
      return;
    }

    try {
      setLoading(true);

      const [lessonComments, bookComments] = await Promise.all([
        fetchAllPages<LessonCommentDto>((pageNumber, pageSize) =>
          commentService.getMyComments({ statusFilter: 'all', includeThreadReplies: true, pageNumber, pageSize }, true)
        ).then(enrichCommentsWithParentWriterNames),
        fetchAllPages<LibraryBookCommentDto>((pageNumber, pageSize) =>
          commentService.getMyLibraryBookComments({ statusFilter: 'all', includeThreadReplies: true, pageNumber, pageSize }, true)
        ).then(enrichCommentsWithParentWriterNames)
      ]);

      const lessonReplyCounts = countCommentDescendantsById(lessonComments);
      const bookReplyCounts = countCommentDescendantsById(bookComments);

      const replyCountObj: Record<string, number> = {};
      for (const [id, cnt] of lessonReplyCounts.entries()) replyCountObj[`lesson:${id}`] = cnt;
      for (const [id, cnt] of bookReplyCounts.entries()) replyCountObj[`book:${id}`] = cnt;
      setReplyCounts(replyCountObj);
      setAllLessonComments(lessonComments);
      setAllBookComments(bookComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      const message = error instanceof Error ? error.message : 'Failed to load comments';
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, showError, fetchAllPages]);

  const commentsByFilter = useMemo(() => {
    const buildItems = (filterOption: CommentFilter): ProfileCommentItem[] => {
      const lessonRows = allLessonComments.filter(
        (c) =>
          matchesProfileCommentStatusFilter(c, filterOption) &&
          isProfileCommentListRow(c, filterOption)
      );
      const bookRows = allBookComments.filter(
        (c) =>
          matchesProfileCommentStatusFilter(c, filterOption) &&
          isProfileCommentListRow(c, filterOption)
      );
      const items: ProfileCommentItem[] = [
        ...lessonRows.map((comment) => ({ source: 'lesson' as const, comment })),
        ...bookRows.map((comment) => ({ source: 'book' as const, comment }))
      ];
      items.sort(
        (a, b) =>
          new Date(b.comment.createdAt).getTime() - new Date(a.comment.createdAt).getTime()
      );
      return items;
    };

    return Object.fromEntries(
      COMMENT_FILTER_ORDER.map((filterOption) => [filterOption, buildItems(filterOption)])
    ) as Record<CommentFilter, ProfileCommentItem[]>;
  }, [allLessonComments, allBookComments]);

  const tabCounts = useMemo(
    () =>
      Object.fromEntries(
        COMMENT_FILTER_ORDER.map((filterOption) => [filterOption, (commentsByFilter[filterOption] ?? []).length])
      ) as Record<CommentFilter, number>,
    [commentsByFilter]
  );

  const commentItems = commentsByFilter[filter] ?? [];

  useEffect(() => {
    setVisibleCounts({
      all: 10,
      pending: 10,
      approved: 10,
      rejected: 10
    });
  }, [allLessonComments, allBookComments]);

  const showMoreForFilter = useCallback(
    (filterOption: CommentFilter, total: number) => {
      setVisibleCounts((prev) => ({
        ...prev,
        [filterOption]: Math.min((prev[filterOption] ?? 10) + 10, total)
      }));
    },
    []
  );

  useEffect(() => {
    if (!active) {
      return;
    }
    if (authLoading) {
      return;
    }
    if (sessionReady) {
      void loadComments();
    }
  }, [active, authLoading, sessionReady, sessionUserId, loadComments]);

  // Refresh counts immediately after a learner creates a new comment/reply
  // (CommentModal dispatches `commentCreated` with { userId, comment }).
  const lastReplyCountReloadRef = useRef<number>(0);
  useEffect(() => {
    if (!active || !sessionReady) return;

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ userId?: string }>;
      const { userId } = customEvent.detail || {};
      if (!userId || userId !== sessionUserId) return;

      const now = Date.now();
      if (now - lastReplyCountReloadRef.current < 1500) return; // avoid double-fire (CommentModal dispatches twice)
      lastReplyCountReloadRef.current = now;
      void loadComments();
    };

    window.addEventListener('commentCreated', handler);
    window.addEventListener('commentUpdated', handler);
    return () => {
      window.removeEventListener('commentCreated', handler);
      window.removeEventListener('commentUpdated', handler);
    };
  }, [active, sessionReady, sessionUserId, loadComments]);

  useLayoutEffect(() => {
    activeFilterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    dragUiRef.current = { ...dragUiRef.current, isDragging };
  }, [isDragging]);

  useLayoutEffect(() => {
    if (!active) return;
    updateIndicator();
    const btn = filterRefs.current[filter];
    const nav = filterNavRef.current;
    const needsFilterNavScroll =
      window.matchMedia('(max-width: 767px)').matches &&
      nav != null &&
      nav.scrollWidth > nav.clientWidth + 1;
    if (btn && nav && needsFilterNavScroll) {
      scrollChildIntoHorizontalScroller(nav, btn, 'smooth');
    }
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [active, filter, updateIndicator, isRtl]);

  useLayoutEffect(() => {
    if (!isDragging) return;
    updateIndicator();
  }, [isDragging, updateIndicator]);

  const syncActiveFilterHeight = useCallback(() => {
    if (!active) return;
    const el = filterContentRefs.current[filter];
    if (!el) return;
    const h = readFilterPaneHeight(el);
    setFilterHeights((prev) => (prev[filter] === h ? prev : { ...prev, [filter]: h }));
  }, [active, filter, readFilterPaneHeight]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !active) return;
    const filterOption = filter;
    const el = filterContentRefs.current[filterOption];
    if (!el) return;
    let raf = 0;
    const obs = new ResizeObserver(() => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const target = filterContentRefs.current[filterOption];
        if (!target || activeFilterRef.current !== filterOption) return;
        const h = readFilterPaneHeight(target);
        setFilterHeights((prev) => (prev[filterOption] === h ? prev : { ...prev, [filterOption]: h }));
      });
    });
    obs.observe(el);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [active, filter, readFilterPaneHeight, loading, commentItems.length]);

  useLayoutEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => {
      schedulePaneHeightSync(syncActiveFilterHeight);
    }, isDragging ? 0 : 300);
    return () => window.clearTimeout(timer);
  }, [active, filter, syncActiveFilterHeight, isDragging, loading, commentItems.length]);

  /** Touch carousel between status filters — same RTL-aware behavior as profile tabs. */
  useEffect(() => {
    if (!active) return;
    const el = cardsScrollRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let dragging = false;
    let axis: 'horizontal' | 'vertical' | null = null;
    const SWIPE_DISTANCE_PX = 50;
    const SWIPE_VELOCITY_PX_PER_MS = 0.4;
    let startTime = 0;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length !== 1) return;
      startX = t.clientX;
      startY = t.clientY;
      lastX = t.clientX;
      startTime = Date.now();
      dragging = false;
      axis = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length !== 1) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      lastX = t.clientX;

      if (axis === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
      }
      if (axis === 'vertical') {
        if (dragging) {
          dragging = false;
          setIsDragging(false);
          dragOffsetPxRef.current = 0;
          dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
          el.style.transition = TAB_CAROUSEL_TRANSITION;
          el.style.setProperty('--comments-filter-drag-x', '0px');
        }
        return;
      }
      if (axis !== 'horizontal') return;

      if (!dragging) {
        dragging = true;
        el.style.transition = 'none';
        setIsDragging(true);
      }
      const idx = COMMENT_FILTER_ORDER.indexOf(activeFilterRef.current);
      const width = el.clientWidth || 1;
      const rtl = isRtlRef.current;
      let offset = dx;
      const atFirstRubber = rtl ? idx === 0 && offset < 0 : idx === 0 && offset > 0;
      const atLastRubber =
        rtl ? idx === COMMENT_FILTER_ORDER.length - 1 && offset > 0 : idx === COMMENT_FILTER_ORDER.length - 1 && offset < 0;
      if (atFirstRubber || atLastRubber) {
        offset = offset / 3;
      }
      offset = Math.max(-width, Math.min(width, offset));
      dragOffsetPxRef.current = offset;
      dragUiRef.current = { isDragging: true, dragOffsetPx: offset };
      el.style.setProperty('--comments-filter-drag-x', `${offset}px`);
      updateIndicatorRef.current();
      e.preventDefault();
    };

    const finishDrag = () => {
      const offset = dragOffsetPxRef.current;
      const width = el.clientWidth || 1;
      const elapsed = Math.max(1, Date.now() - startTime);
      const velocity = (lastX - startX) / elapsed;
      const idx = COMMENT_FILTER_ORDER.indexOf(activeFilterRef.current);
      const rtl = isRtlRef.current;
      let nextIdx = idx;
      if (rtl) {
        if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx < COMMENT_FILTER_ORDER.length - 1) {
          nextIdx = idx + 1;
        } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
          nextIdx = idx - 1;
        } else if (Math.abs(offset) > width / 2) {
          nextIdx = offset > 0
            ? Math.min(COMMENT_FILTER_ORDER.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        }
      } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx < COMMENT_FILTER_ORDER.length - 1) {
        nextIdx = idx + 1;
      } else if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
        nextIdx = idx - 1;
      } else if (Math.abs(offset) > width / 2) {
        nextIdx = offset < 0
          ? Math.min(COMMENT_FILTER_ORDER.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      }
      const nextFilter = COMMENT_FILTER_ORDER[nextIdx];
      const currentFilter = activeFilterRef.current;
      const fromCarousel = filterCarouselIndexRef.current(currentFilter);
      const toCarousel = nextFilter ? filterCarouselIndexRef.current(nextFilter) : fromCarousel;

      if (nextFilter && nextFilter !== currentFilter) {
        settleCarouselDrag({
          track: el,
          dragVar: '--comments-filter-drag-x',
          fromCarouselIndex: fromCarousel,
          toCarouselIndex: toCarousel,
          offsetPx: offset,
          paneWidthPx: width,
          onSettling: () => {
            activeFilterRef.current = nextFilter;
            dragOffsetPxRef.current = offset + (toCarousel - fromCarousel) * width;
            dragUiRef.current = { isDragging: true, dragOffsetPx: dragOffsetPxRef.current };
            setFilter(nextFilter);
          },
          onReleased: () => {
            dragOffsetPxRef.current = 0;
            dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
            setIsDragging(false);
            updateIndicatorRef.current();
          }
        });
      } else {
        el.style.transition = TAB_CAROUSEL_TRANSITION;
        el.style.setProperty('--comments-filter-drag-x', '0px');
        dragOffsetPxRef.current = 0;
        dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
        setIsDragging(false);
        queueMicrotask(() => updateIndicatorRef.current());
      }
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      finishDrag();
    };

    const onTouchCancel = () => {
      if (!dragging) return;
      dragging = false;
      dragOffsetPxRef.current = 0;
      dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
      el.style.transition = TAB_CAROUSEL_TRANSITION;
      el.style.setProperty('--comments-filter-drag-x', '0px');
      setIsDragging(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [active]);

  const getStatusIcon = (isApproved: boolean | null | undefined) => {
    if (isApproved === null || isApproved === undefined) {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
    if (isApproved) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusText = (isApproved: boolean | null | undefined) => {
    if (isApproved === null || isApproved === undefined) return t('comments.pending');
    if (isApproved) return t('comments.approved');
    return t('comments.rejected');
  };

  const filterLabel = (filterOption: (typeof COMMENT_FILTER_ORDER)[number]) => {
    const key = `comments.filter${filterOption.charAt(0).toUpperCase()}${filterOption.slice(1)}` as
      | 'comments.filterAll'
      | 'comments.filterPending'
      | 'comments.filterApproved'
      | 'comments.filterRejected';
    return t(key);
  };

  const filterDescription = (filterOption: CommentFilter) => {
    const keys: Record<CommentFilter, 'comments.filterAllDescription' | 'comments.filterPendingDescription' | 'comments.filterApprovedDescription' | 'comments.filterRejectedDescription'> = {
      all: 'comments.filterAllDescription',
      pending: 'comments.filterPendingDescription',
      approved: 'comments.filterApprovedDescription',
      rejected: 'comments.filterRejectedDescription'
    };
    return t(keys[filterOption]);
  };

  const handleCardOpenLessonComments = (comment: LessonCommentDto) => {
    const id = comment.lessonId;
    if (!id) return;
    setCommentModalBook(null);
    setCommentModalLesson({
      lessonId: id,
      lessonTitle: displayLessonTitle(comment),
      focusCommentId: comment.id
    });
  };

  const handleCardOpenBookComments = (comment: LibraryBookCommentDto) => {
    const id = comment.libraryBookId;
    if (!id) return;
    setCommentModalLesson(null);
    setCommentModalBook({
      libraryBookId: id,
      bookTitle: displayBookTitle(comment),
      focusCommentId: comment.id
    });
  };

  const renderThreadBadges = (
    comment: { id: number; parentCommentId?: number | null; parentWriterName?: string },
    source: 'lesson' | 'book'
  ) => {
    const replyCount = replyCounts[`${source}:${comment.id}`] ?? 0;
    return (
      <>
        {comment.parentCommentId != null && (
          <span className="inline-flex max-w-full items-center rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
            {t('comments.inReplyTo', { name: comment.parentWriterName || t('comments.anonymous') })}
          </span>
        )}
        {replyCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
            <MessageSquare className="h-3 w-3 text-indigo-500 dark:text-indigo-300" />
            {replyCount}
          </span>
        )}
      </>
    );
  };

  const handleCloseCommentModal = useCallback(() => {
    setCommentModalLesson(null);
    setCommentModalBook(null);
    if (active && sessionReady) {
      void loadComments();
    }
  }, [active, sessionReady, loadComments]);

  const handleViewLessonLink = (e: React.MouseEvent, comment: LessonCommentDto) => {
    e.stopPropagation();
    if (!onNavigateToLesson) return;
    if (comment.isAccessible === false) {
      showError(t('lesson.noLongerAvailable'));
      return;
    }
    onNavigateToLesson(comment.lessonId);
    onAfterNavigateToLesson?.();
  };

  const handleViewBookLink = (e: React.MouseEvent, comment: LibraryBookCommentDto) => {
    e.stopPropagation();
    if (!onNavigateToBook) return;
    if (comment.isAccessible === false) {
      showError(t('book.noLongerAvailable'));
      return;
    }
    onNavigateToBook(comment.libraryBookId);
    onAfterNavigateToLesson?.();
  };

  const rootClassName = embedded
    ? `flex flex-col ${className}`
    : `flex flex-col min-h-0 ${className}`;
  const panePaddingClass = embedded
    ? 'touch-auto overscroll-x-none overflow-x-clip p-4'
    : 'overscroll-x-none p-4';

  const activePaneIsEmpty = !loading && commentItems.length === 0;

  const renderCommentList = (filterOption: CommentFilter) => {
    const isActivePane = filterOption === filter;
    const items = commentsByFilter[filterOption] ?? [];
    const visibleCount = visibleCounts[filterOption] ?? 10;
    const visibleItems = items.slice(0, visibleCount);
    const paneLoading = isActivePane && loading;

    if (paneLoading && items.length === 0) {
      return <LearnerInlineContentLoader message={t('comments.loadingUserComments')} />;
    }
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8">
          <MessageSquare className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500 sm:mb-4 sm:h-12 sm:w-12" />
          <p className="text-center text-sm text-gray-600 dark:text-gray-400 sm:text-base">{t('comments.noUserComments')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {visibleItems.map((item) => {
          if (item.source === 'lesson') {
            const comment = item.comment;
            const isRateOnly = !!(comment.ratingOnly || (comment.rating && !comment.commentText?.trim()));
            const canOpenComments = !!comment.lessonId;
            const lessonTitle = displayLessonTitle(comment);
            const lessonContextPath = buildLessonContextPath(comment);
            return (
              <div
                key={`lesson-${comment.id}`}
                {...(canOpenComments ? { role: 'button' as const, tabIndex: 0 } : {})}
                onClick={() => canOpenComments && handleCardOpenLessonComments(comment)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && canOpenComments) {
                    e.preventDefault();
                    handleCardOpenLessonComments(comment);
                  }
                }}
              className={`rounded-lg border border-gray-200 bg-gray-50 p-4 transition-all hover:border-purple-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-700/50 dark:hover:border-purple-600 ${
                canOpenComments ? 'cursor-pointer' : ''
              } ${comment.isAccessible === false ? 'opacity-50' : ''}`}
                dir={direction}
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 text-start">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {getStatusIcon(comment.isApproved)}
                      <span
                        className={`text-xs font-medium ${
                          comment.isApproved === null || comment.isApproved === undefined
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : comment.isApproved
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                      {getStatusText(comment.isApproved)}
                    </span>
                    <span className="rounded border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-900 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
                      {t('comments.lessonBadge')}
                    </span>
                    {isRateOnly && (
                        <span className="rounded border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                          {t('comments.onlyRateBadge')}
                        </span>
                      )}
                      {renderThreadBadges(comment, 'lesson')}
                    </div>
                    <h3 className="mb-1 font-semibold text-gray-900 dark:text-gray-100">
                      {lessonTitle}
                    </h3>
                    {lessonContextPath && (
                      <FlowContextBreadcrumb
                        contextSubtitle={lessonContextPath.contextSubtitle}
                        contextSubtitleFa={lessonContextPath.contextSubtitleFa}
                        isRtl={isRtl}
                        className="text-xs text-gray-500 dark:text-gray-400"
                      />
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatCommentDate(comment.createdAt)}
                    </p>
                  </div>
                  {comment.rating && (
                    <div className="flex shrink-0 items-center gap-1 self-start">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{comment.rating}</span>
                    </div>
                  )}
                </div>

                {comment.commentText && (
                  <p className="mb-2 line-clamp-2 text-start text-sm text-gray-700 dark:text-gray-300">{comment.commentText}</p>
                )}

                {onNavigateToLesson && comment.lessonId && (
                  <div className="flex justify-end text-xs text-gray-500 dark:text-gray-400">
                    <button
                      type="button"
                      onClick={(e) => handleViewLessonLink(e, comment)}
                      className={`${viewLinkClass} text-purple-600 dark:text-purple-400`}
                    >
                      {renderViewLinkLabel(t('comments.clickViewLesson'))}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          const comment = item.comment;
          const isRateOnly = !!(comment.ratingOnly || (comment.rating && !comment.commentText?.trim()));
          const canOpenComments = !!comment.libraryBookId;
          return (
            <div
              key={`book-${comment.id}`}
              {...(canOpenComments ? { role: 'button' as const, tabIndex: 0 } : {})}
              onClick={() => canOpenComments && handleCardOpenBookComments(comment)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && canOpenComments) {
                  e.preventDefault();
                  handleCardOpenBookComments(comment);
                }
              }}
              className={`rounded-lg border border-gray-200 bg-gray-50 p-4 transition-all hover:border-indigo-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-700/50 dark:hover:border-indigo-600 ${
                canOpenComments ? 'cursor-pointer' : ''
              } ${comment.isAccessible === false ? 'opacity-50' : ''}`}
              dir={direction}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 text-start">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    {getStatusIcon(comment.isApproved)}
                    <span
                      className={`text-xs font-medium ${
                        comment.isApproved === null || comment.isApproved === undefined
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : comment.isApproved
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {getStatusText(comment.isApproved)}
                    </span>
                    <span className="rounded border border-teal-200 bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-900 dark:border-teal-700 dark:bg-teal-900/40 dark:text-teal-200">
                      {t('comments.bookBadge')}
                    </span>
                    {isRateOnly && (
                      <span className="rounded border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                        {t('comments.onlyRateBadge')}
                      </span>
                    )}
                    {renderThreadBadges(comment, 'book')}
                  </div>
                  <h3 className="mb-1 font-semibold text-gray-900 dark:text-gray-100">
                    {displayBookTitle(comment)}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {formatCommentDate(comment.createdAt)}
                  </p>
                </div>
                {comment.rating && (
                  <div className="flex shrink-0 items-center gap-1 self-start">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{comment.rating}</span>
                  </div>
                )}
              </div>

              {comment.commentText && (
                <p className="mb-2 line-clamp-2 text-start text-sm text-gray-700 dark:text-gray-300">{comment.commentText}</p>
              )}

              {onNavigateToBook && comment.libraryBookId && (
                <div className="flex justify-end text-xs text-gray-500 dark:text-gray-400">
                  <button
                    type="button"
                    onClick={(e) => handleViewBookLink(e, comment)}
                    className={`${viewLinkClass} text-indigo-600 dark:text-indigo-400`}
                  >
                    {renderViewLinkLabel(t('comments.clickViewBook'))}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {visibleItems.length < items.length && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showMoreForFilter(filterOption, items.length);
              }}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-pink-700 hover:shadow-xl dark:from-purple-500 dark:to-pink-500 dark:hover:from-purple-600 dark:hover:to-pink-600"
            >
              <span>{t('messages:loadMore')}</span>
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <div className={rootClassName}>
      <div
        data-no-tab-swipe
        dir="ltr"
        className="-mx-1 flex min-w-0 shrink-0 touch-auto overscroll-x-none border-b border-gray-200 px-1 pb-px dark:border-gray-700"
      >
        <nav
          ref={filterNavRef}
          className="mx-auto min-w-0 max-w-full overflow-x-auto overflow-y-clip scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div
            ref={filterNavRowRef}
            role="tablist"
            aria-label={filterLabel('all')}
            className="relative mx-auto flex min-w-max flex-nowrap gap-0"
          >
            {COMMENT_FILTER_ORDER.map((filterOption) => (
              <button
                key={filterOption}
                ref={(el) => {
                  if (el) filterRefs.current[filterOption] = el;
                }}
                type="button"
                role="tab"
                aria-selected={filter === filterOption ? true : false}
                style={{ order: filterVisualOrder(filterOption) }}
                onClick={() => handleFilterChange(filterOption)}
                className={`shrink-0 border-b-2 border-transparent px-3 text-xs font-medium transition-colors sm:px-5 sm:text-sm ${
                  embedded ? 'py-2' : 'py-3'
                } ${
                  filter === filterOption
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                {`${filterLabel(filterOption)} (${tabCounts[filterOption] ?? 0})`}
              </button>
            ))}
            <div
              ref={(el) => {
                filterIndicatorElRef.current = el;
                if (el) queueMicrotask(() => updateIndicatorRef.current());
              }}
              className="absolute bottom-0 left-0 h-0.5 w-0 bg-purple-600 dark:bg-purple-400 pointer-events-none"
              style={{ zIndex: 10 }}
            />
          </div>
        </nav>
      </div>

      <div
        dir="ltr"
        data-no-tab-swipe
        className={`min-h-0 overflow-x-hidden ${
          embedded
            ? 'overflow-y-hidden'
            : `flex-1 ${activePaneIsEmpty ? 'overflow-y-hidden' : 'overflow-y-auto overscroll-y-contain'}`
        }`}
        style={
          embedded
            ? {
                height: filterHeights[filter] !== undefined ? `${filterHeights[filter]}px` : 'auto',
                transition: 'none'
              }
            : undefined
        }
      >
        <div
          ref={cardsScrollRef}
          className="flex w-full items-start"
          style={{
            touchAction: isDragging ? 'none' : activePaneIsEmpty ? 'pan-x pan-y' : 'pan-y',
            transform: `translate3d(calc(-${filterCarouselIndex(filter) * 100}% + var(--comments-filter-drag-x, 0px)), 0, 0)`,
            transition: isDragging ? 'none' : TAB_CAROUSEL_TRANSITION
          }}
        >
          {filterCarouselOrder.map((filterOption) => (
            <div
              key={filterOption}
              role="tabpanel"
              aria-label={`${filterOption} comments`}
              className="min-w-0 flex-[0_0_100%] shrink-0 self-start"
            >
              <div
                ref={(el) => {
                  filterContentRefs.current[filterOption] = el;
                }}
                dir={direction}
                className={panePaddingClass}
              >
                <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400 sm:text-sm">
                  {filterDescription(filterOption)}
                </p>
                {renderCommentList(filterOption)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    {commentModalLesson &&
      typeof document !== 'undefined' &&
      createPortal(
        <CommentModal
          lessonId={commentModalLesson.lessonId}
          lessonTitle={commentModalLesson.lessonTitle}
          focusCommentId={commentModalLesson.focusCommentId}
          isOpen
          onClose={handleCloseCommentModal}
        />,
        document.body
      )}
    {commentModalBook &&
      typeof document !== 'undefined' &&
      createPortal(
        <CommentModal
          libraryBookId={commentModalBook.libraryBookId}
          bookTitle={commentModalBook.bookTitle}
          focusCommentId={commentModalBook.focusCommentId}
          isOpen
          onClose={handleCloseCommentModal}
        />,
        document.body
      )}
    </>
  );
};

interface UserCommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
}

export const UserCommentsModal: React.FC<UserCommentsModalProps> = ({
  isOpen,
  onClose,
  onNavigateToLesson
}) => {
  const { isMobile } = useResponsive();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl ${
          isMobile ? 'w-full h-full max-h-full' : 'w-full max-w-3xl max-h-[90vh]'
        } flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">My Comments</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <UserCommentsPanel
          active={isOpen}
          onNavigateToLesson={onNavigateToLesson}
          onAfterNavigateToLesson={onClose}
          className="flex-1 min-h-0"
        />
      </div>
    </div>
  );
};
